import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { Lock, Mail, Phone, LogOut, AlertTriangle } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_monitor-adybo/artifacts/tadicmii_Adybo%20Logo%20Colori%20Bianco%20R.png";
const AUTO_LOGOUT_SECONDS = 10;

export default function Suspended() {
  const { user, logout } = useAuth();
  const [countdown, setCountdown] = useState(AUTO_LOGOUT_SECONDS);

  // Auto-logout dopo N secondi — in modo che l'utente non resti con una sessione
  // "appesa" sulla pagina di sospensione (safety net richiesto da UX).
  useEffect(() => {
    if (!user || user === false) return;
    if (countdown <= 0) {
      logout();
      return;
    }
    const t = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, user, logout]);

  // Se l'utente non è autenticato → login. Se è staff o non sospeso → home.
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-400">Caricamento…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "cliente") return <Navigate to="/" replace />;
  if (!user.is_suspended) return <Navigate to="/" replace />;

  const isExpired = user.suspension_reason === "expired";
  const subEnd = user.company_subscription_end || user.subscription_end;
  const fmtEnd = subEnd ? new Date(String(subEnd).slice(0, 10)).toLocaleDateString("it-IT") : null;

  return (
    <div className="min-h-screen bg-[#071B4D] text-white flex items-center justify-center p-6" data-testid="suspended-page">
      {/* Soft radial gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#071B4D] via-[#0b2666] to-[#1a1550]" />
      <div className="relative z-10 max-w-xl w-full">
        <div className="flex justify-center mb-8">
          <img src={LOGO} alt="Adybo" className="h-10 opacity-90" />
        </div>
        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center mb-5">
            <Lock size={26} className="text-amber-300" />
          </div>
          <h1 className="font-display text-3xl font-bold text-center tracking-tight">
            {isExpired ? "Abbonamento scaduto" : "Accesso sospeso"}
          </h1>
          <p className="text-center text-sm text-white/70 mt-3 leading-relaxed">
            {isExpired ? (
              <>
                <b>Abbonamento scaduto</b>
                {fmtEnd ? <> il <b>{fmtEnd}</b></> : null}: l&apos;accesso all&apos;account è stato <b>sospeso temporaneamente</b>.
                <br />Contatta Adybo per rinnovare il contratto e ripristinare l&apos;accesso al portale.
              </>
            ) : (
              <>
                L&apos;accesso all&apos;account è stato <b>sospeso temporaneamente</b>.
                <br />Contatta Adybo per ulteriori informazioni e per il ripristino dell&apos;accesso.
              </>
            )}
          </p>

          {/* Contatti Adybo */}
          <div className="mt-6 grid grid-cols-1 gap-2.5">
            <a
              href="mailto:direzione@adybo.it"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
              data-testid="suspended-email-link"
            >
              <Mail size={16} className="text-blue-300" />
              <span className="text-sm">direzione@adybo.it</span>
            </a>
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10">
              <Phone size={16} className="text-blue-300" />
              <span className="text-sm">Adybo S.r.l. — Via Aicardo, 2 · 20141 Milano</span>
            </div>
          </div>

          {/* Auto-logout countdown */}
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="suspended-countdown">
            <AlertTriangle size={12} />
            Verrai disconnesso automaticamente tra <b>{countdown}s</b>
          </div>

          <button
            onClick={logout}
            className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white text-slate-900 font-semibold text-sm hover:bg-white/90 transition-colors"
            data-testid="suspended-logout-btn"
          >
            <LogOut size={14} /> Disconnettiti ora
          </button>
        </div>
        <div className="text-center text-[11px] text-white/40 mt-6">
          Utente: {user.email}
          {user.company_name ? ` · ${user.company_name}` : null}
        </div>
      </div>
    </div>
  );
}
